'use client'

import { useCallback, useState } from 'react'
import { useTranslations } from 'next-intl'
import cn from 'classnames'

import shared from '@booking/index.module.scss'
import styles from './index.module.scss'

import SummaryBox from '@booking/components/SummaryBox'
import HelpBox from '@booking/components/HelpBox'
import BackButton from '@booking/components/BackButton'
import ContinueButton from '@booking/components/ContinueButton'
import RestrictionsModal from '../QuantitySelection/components/RestrictionsModal'

import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'
import type { VoucherQuantities } from '@booking/types/booking'

import controlStyles from '../QuantitySelection/components/TicketType/index.module.scss'
import MinusIcon from '../QuantitySelection/components/TicketType/minus-icon.svg'
import PlusIcon from '../QuantitySelection/components/TicketType/plus-icon.svg'

// ---------------------------------------------------------------------------

type VoucherTypeConfig = {
  category: string
  labelKey: string
  price: number
}

const VOUCHER_TYPES: VoucherTypeConfig[] = [
  { category: 'adult', labelKey: 'voucher.selectQuantity.adult', price: 26 },
  { category: 'child', labelKey: 'voucher.selectQuantity.child', price: 18 },
]

const MAX_PER_VOUCHER = 12

// ---------------------------------------------------------------------------

export default function VoucherSelection() {
  const t = useTranslations('booking')
  const state = useBookingState()
  const { dispatch, onCompleteVoucherSelection } = useBookingActionsCtx()

  const [showError, setShowError] = useState(false)
  const [restrictionsOpen, setRestrictionsOpen] = useState(false)

  const getValue = (category: string) =>
    state.voucherQuantities.find((q) => q.category === category)?.quantity ?? 0

  const setNewValue = useCallback(
    (category: string, next: number) => {
      const existing = state.voucherQuantities.find((q) => q.category === category)
      if (existing?.quantity === next) return

      const nextQuantities: VoucherQuantities = state.voucherQuantities.map((q) =>
        q.category === category ? { ...q, quantity: next } : q,
      )
      if (!nextQuantities.find((q) => q.category === category)) {
        nextQuantities.push({ category, quantity: next })
      }

      dispatch({ type: 'SET_VOUCHER_QUANTITIES', voucherQuantities: nextQuantities })
      if (showError) setShowError(false)
    },
    [dispatch, state.voucherQuantities, showError],
  )

  const onContinue = useCallback(() => {
    const anySelected = state.voucherQuantities.some((q) => q.quantity > 0)
    if (!anySelected) {
      setShowError(true)
      return
    }
    return onCompleteVoucherSelection()
  }, [state.voucherQuantities, onCompleteVoucherSelection])

  return (
    <div className={cn(shared.box)}>
      <RestrictionsModal open={restrictionsOpen} onOpenChange={setRestrictionsOpen} />

      <div className={cn(shared.inner)}>
        <div className={cn(shared.leftColumn)}>
          <BackButton />
          <h2 className={styles.title}>{t('voucher.title')}</h2>

          <div
            className={styles.content}
            dangerouslySetInnerHTML={{ __html: t.raw('voucher.content') as string }}
          />

          <button
            type="button"
            className={styles.ageRestrictLink}
            onClick={() => setRestrictionsOpen(true)}
          >
            {t('voucher.selectQuantity.ageRestrict')}
          </button>

          {showError && <div className={styles.error}>{t('ticket.selectQuantity.error')}</div>}

          <div className={styles.rows}>
            {VOUCHER_TYPES.map(({ category, labelKey, price }) => {
              const value = getValue(category)
              return (
                <div key={category} className={styles.row}>
                  <div className={styles.rowLeft}>
                    <span className={styles.rowName}>{t(labelKey)}</span>
                    <span className={styles.rowPrice}>
                      {price}€{' '}
                      <span className={styles.rowPricePer}>{t('ticket.summaryBox.perPerson')}</span>
                    </span>
                  </div>
                  <span className={cn(controlStyles.controlsHolder)}>
                    <button
                      type="button"
                      className={cn(controlStyles.buttonMath, controlStyles.buttonMinus)}
                      disabled={value <= 0}
                      onClick={() => setNewValue(category, value - 1)}
                      aria-label={`Decrease ${category}`}
                    >
                      <MinusIcon />
                    </button>
                    <span className={controlStyles.controlValue}>{value}</span>
                    <button
                      type="button"
                      className={cn(controlStyles.buttonMath, controlStyles.buttonPlus)}
                      disabled={value >= MAX_PER_VOUCHER}
                      onClick={() => setNewValue(category, value + 1)}
                      aria-label={`Increase ${category}`}
                    >
                      <PlusIcon />
                    </button>
                  </span>
                </div>
              )
            })}
          </div>

          <ContinueButton className={cn(shared.continue)} onContinue={onContinue} />
        </div>

        <div className={cn(shared.rightColumn)}>
          <SummaryBox onContinue={onContinue} />
          <HelpBox />
        </div>
      </div>
    </div>
  )
}
